import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { InlineLoadSkeleton } from "../components/common/InlineLoadSkeleton";
import toast from "react-hot-toast";
import {
  validateClientName,
  CLIENT_NAME_MAX_LENGTH,
} from "../utils/validationUtils";
import { userServiceAPI } from "../services/api";

const SignupCompletePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    client_name: "",
  });
  const [errors, setErrors] = useState({});

  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const domain = searchParams.get("domain");

  useEffect(() => {
    // If no token, redirect to login
    if (!token) {
      toast.error("Invalid signup link");
      navigate("/login");
    }
  }, [token, navigate]);

  const validateForm = () => {
    const newErrors = {};
    const clientNameError = validateClientName(formData.client_name);
    if (clientNameError) newErrors.client_name = clientNameError;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "client_name" && value.length > CLIENT_NAME_MAX_LENGTH) return;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Complete signup - this creates client and user in the background
      // Use user service API (port 3001) instead of backend service (port 3000)
      const response = await userServiceAPI.post("/api/auth/signup/complete", {
        token,
        client_name: formData.client_name.trim(),
        email,
      });

      if (response.data.success) {
        const { token: authToken, user, client } = response.data.data;

        // Validate that we have the required data
        if (!authToken || !user) {
          throw new Error("Invalid response: missing token or user data");
        }

        // Use loginWithGoogle to set up auth context (same as Google SSO login)
        await loginWithGoogle(authToken, user);

        toast.success(
          `Welcome! Your workspace "${client?.client_name || "Unknown"}" has been created.`,
        );

        // Navigate to projects page
        navigate("/projects", { replace: true });
      } else {
        throw new Error(response.data.message || "Failed to complete signup");
      }
    } catch (error) {
      console.error("Signup completion error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to complete signup. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Complete Your Signup
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Create your workspace to get started
          </p>
          {email && (
            <p className="mt-1 text-sm text-gray-500">
              Signing up as: <span className="font-medium">{email}</span>
            </p>
          )}
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <Label htmlFor="client_name" className="sr-only">
                Workspace Name
              </Label>
              <Input
                id="client_name"
                name="client_name"
                type="text"
                required
                value={formData.client_name}
                onChange={handleInputChange}
                placeholder="Letters, numbers, spaces, . - ' & , ( )"
                maxLength={CLIENT_NAME_MAX_LENGTH}
                className={`appearance-none rounded-md relative block w-full px-3 py-3 border ${
                  errors.client_name ? "border-red-500" : "border-gray-300"
                } placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm`}
                disabled={loading}
              />
              {errors.client_name && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.client_name}
                </p>
              )}
            </div>
          </div>

          <div>
            <Button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <InlineLoadSkeleton className="h-5 w-5 mr-2" />
                  Creating Workspace...
                </>
              ) : (
                "Create Workspace"
              )}
            </Button>
          </div>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              By continuing, you agree to create a new workspace for your
              organization.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupCompletePage;
